import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMenuAlt3, HiX } from 'react-icons/hi';

const navLinks = [
  { name: 'About',      href: '#about'      },
  { name: 'Stack',      href: '#skills'     },
  { name: 'Projects',   href: '#projects'   },
  { name: 'Experience', href: '#experience' },
  { name: 'Contact',    href: '#contact'    },
];

export default function Navbar() {
  const [isOpen,    setIsOpen]    = useState(false);
  const [scrolled,  setScrolled]  = useState(false);
  const [activeSection, setActiveSection] = useState('');

  // Shrink pill on scroll
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Active section tracking via scroll position — picks the section
  // whose top edge is closest to (but at or above) the navbar bottom.
  useEffect(() => {
    const sectionIds = ['home', 'about', 'skills', 'projects', 'experience', 'contact'];
    const OFFSET = 100; // px below navbar to use as trigger line

    const getActiveSection = () => {
      const scrollY = window.scrollY + OFFSET;
      let current = sectionIds[0];

      for (const id of sectionIds) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= scrollY) {
          current = id;
        }
      }
      setActiveSection(current);
    };

    getActiveSection(); // run on mount
    window.addEventListener('scroll', getActiveSection, { passive: true });
    return () => window.removeEventListener('scroll', getActiveSection);
  }, []);

  const handleNavClick = (href) => {
    setIsOpen(false);
    const target = document.querySelector(href);
    if (target) target.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Main floating nav */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-0 left-0 right-0 z-50 flex justify-center"
        style={{ paddingTop: scrolled ? '12px' : '20px', transition: 'padding 0.3s ease' }}
      >
        <div
          className="relative flex items-center gap-1 md:gap-2"
          style={{
            background: scrolled
              ? 'rgba(8, 15, 23, 0.92)'
              : 'rgba(13, 20, 29, 0.7)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '9999px',
            padding: scrolled ? '0.35rem 0.75rem' : '0.5rem 1rem',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.06)',
            transition: 'all 0.3s ease',
          }}
        >
          {/* Logo */}
          <a
            href="#home"
            onClick={(e) => { e.preventDefault(); handleNavClick('#home'); }}
            className="flex items-center gap-1 mr-2 md:mr-4 no-underline"
            style={{ textDecoration: 'none' }}
          >
            <span
              style={{
                fontFamily: 'Sora, sans-serif',
                fontWeight: 800,
                fontSize: scrolled ? '0.9rem' : '1rem',
                letterSpacing: '-0.03em',
                color: '#dce3f0',
                transition: 'font-size 0.3s',
              }}
            >
              ATMAN
            </span>
            <span style={{ color: '#00f2ff', fontWeight: 800, fontSize: scrolled ? '0.9rem' : '1rem' }}>.</span>
          </a>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const sectionId = link.href.replace('#', '');
              const isActive = activeSection === sectionId;
              return (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                  className="relative no-underline"
                  style={{
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.82rem',
                    padding: '0.4rem 0.9rem',
                    borderRadius: '9999px',
                    color: isActive ? '#00f2ff' : '#b9cacb',
                    background: isActive ? 'rgba(0, 242, 255, 0.08)' : 'transparent',
                    transition: 'all 0.2s ease',
                    textDecoration: 'none',
                    letterSpacing: '0.01em',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = '#dce3f0';
                      e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) {
                      e.currentTarget.style.color = '#b9cacb';
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  {link.name}
                </a>
              );
            })}
          </div>

          {/* CTA */}
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); handleNavClick('#contact'); }}
            className="hidden md:inline-flex items-center gap-1.5 ml-2 no-underline"
            style={{
              fontFamily: 'Sora, sans-serif',
              fontWeight: 600,
              fontSize: '0.82rem',
              padding: '0.45rem 1.1rem',
              borderRadius: '9999px',
              background: '#00f2ff',
              color: '#080f17',
              textDecoration: 'none',
              transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
              letterSpacing: '-0.01em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 0 20px rgba(0, 242, 255, 0.5)';
              e.currentTarget.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = 'none';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            Let's Talk
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="md:hidden p-2 rounded-full"
            style={{
              background: 'transparent',
              border: 'none',
              color: '#b9cacb',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            aria-label="Toggle menu"
          >
            {isOpen ? <HiX size={20} /> : <HiMenuAlt3 size={20} />}
          </button>
        </div>
      </motion.nav>

      {/* Mobile drawer overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-0 z-40"
              style={{ background: 'rgba(8, 15, 23, 0.8)', backdropFilter: 'blur(8px)' }}
              onClick={() => setIsOpen(false)}
            />
            {/* Drawer */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="fixed top-0 right-0 bottom-0 z-50 flex flex-col"
              style={{
                width: '280px',
                background: 'rgba(13, 20, 29, 0.98)',
                backdropFilter: 'blur(24px)',
                borderLeft: '1px solid rgba(255,255,255,0.08)',
                padding: '5rem 2rem 2rem',
              }}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-6 right-6"
                style={{ background: 'none', border: 'none', color: '#b9cacb', cursor: 'pointer' }}
              >
                <HiX size={24} />
              </button>

              <nav className="flex flex-col gap-2">
                {navLinks.map((link, i) => (
                  <motion.a
                    key={link.name}
                    href={link.href}
                    onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: i * 0.06, ease: 'easeOut' }}
                    className="no-underline"
                    style={{
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 600,
                      fontSize: '1.25rem',
                      color: activeSection === link.href.replace('#', '') ? '#00f2ff' : '#dce3f0',
                      padding: '0.75rem 0',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                      textDecoration: 'none',
                    }}
                  >
                    {link.name}
                  </motion.a>
                ))}
              </nav>

              <motion.a
                href="#contact"
                onClick={(e) => { e.preventDefault(); handleNavClick('#contact'); }}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="btn-primary justify-center mt-8 no-underline"
                style={{ textDecoration: 'none' }}
              >
                Let's Talk →
              </motion.a>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
